/**
 * Authenticated Service Provider - Tier 2 (Post-Login Services)
 * 
 * Loads services needed for authenticated workspace functionality.
 * Only mounts after successful login. Shows boot sequence during initialization.
 * Target: 1-2s initialization with engaging UI feedback
 * 
 * Services included:
 * - Todo/Task Management
 * - Realtime Subscriptions
 * - Notifications
 * - Gamification
 * - Time Management
 * - Navigation
 * 
 * Phase 4 Architecture - Performance Optimization
 */

import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { serviceRegistry } from '@/services/core/service-registry';
import { typedSupabase } from '@/lib/typed-supabase';
import { TodoService } from '@/services/core/todo.service';
import { gamificationServiceV2 } from '@/services/core/gamification-v2.service';
import { UnifiedTaskService } from '@/services/core/unified-task.service';
import { NavigationService } from "@flowstate/ui";
import { TimeBlockService } from '@/services/features/time-block.service';
import { TaskEstimationService } from '@/services/features/task-estimation.service';
import { TaskDependencyService } from '@/services/features/task-dependency.service';
import { RecurrenceService } from '@/services/features/recurrence.service';
import { WorkloadTrackingService } from '@/services/features/workload-tracking.service';
import { logger } from "@/lib/logger";
import { AuditService } from '@/services/admin/auditService';
import { UserService } from '@/services/admin/userService';

interface AuthenticatedServiceProviderProps {
  children: ReactNode;
  onInitProgress?: (step: string, progress: number) => void;
}

// Service configuration helper
const getServiceConfig = (serviceName: string) => ({
  lazy: false,
  singleton: true,
  timeout: 10000,
  retries: 3,
  dependencies: [] as string[],
  metadata: { name: serviceName, version: '1.0.0', tier: 'authenticated' },
  features: {},
  limits: {},
});

// Singleton promise to prevent double-initialization
let initializationPromise: Promise<void> | null = null;
let servicesFullyInitialized = false;

const initializeAuthenticatedServices = (
  onProgress?: (step: string, progress: number) => void
): Promise<void> => {
  // If we have an active promise, return it (singleton pattern)
  // This handles multiple calls including StrictMode
  if (initializationPromise) {
    if (servicesFullyInitialized && onProgress) {
        onProgress('Ready!', 100);
    }
    return initializationPromise;
  }

  initializationPromise = (async () => {
    const startTime = performance.now();
    logger.info('🚀 Initializing Tier 2 (Authenticated) services...');

    try {
      onProgress?.('Initializing task management...', 10);
      logger.debug('Registering todo service');

      // Todo Service (Gravitational Center)
      serviceRegistry.register(
        'todo',
        () => new TodoService(),
        getServiceConfig('todo')
      );
      await serviceRegistry.get('todo');
      logger.debug('Todo service initialized');

      onProgress?.('Setting up notifications...', 30);
      logger.debug('Loading notification manager');

      // Notification Manager Service - Import directly
      const { notificationManager } = await import('@flowstate/core');
      serviceRegistry.register(
        'notifications',
        () => notificationManager,
        getServiceConfig('notifications')
      );
      await serviceRegistry.get('notifications');
      logger.debug('Notification service initialized');

      onProgress?.('Loading gamification system...', 45);
      logger.debug('Registering gamification service');

      serviceRegistry.register(
        'gamification',
        () => gamificationServiceV2,
        getServiceConfig('gamification')
      );

      onProgress?.('Configuring workspace...', 60);
      logger.debug('Setting up UnifiedTaskService and auth adapter');

      // Create auth adapter for UnifiedTaskService (uses typedSupabase directly; no 'auth' service in registry)
      const authAdapter = {
        async getCurrentUser(): Promise<{ id: string } | undefined> {
          try {
            const { data: { user } } = await typedSupabase.auth.getUser();
            return user ? { id: user.id } : undefined;
          } catch (error) {
            logger.error('Error getting current user:', { error: error });
            return undefined;
          }
        }
      };
      
      serviceRegistry.register(
        'unified-tasks',
        () => new UnifiedTaskService(
          typedSupabase,
          authAdapter,
          serviceRegistry.getSync<TodoService>('todo')
        ),
        getServiceConfig('unified-tasks')
      );

      onProgress?.('Initializing navigation...', 75);
      logger.debug('Registering navigation service');

      serviceRegistry.register(
        'navigation',
        () => new NavigationService(),
        getServiceConfig('navigation')
      );

      onProgress?.('Loading time management...', 85);
      logger.debug('Registering time management services');

      serviceRegistry.register('time-block', () => new TimeBlockService(), getServiceConfig('time-block'));
      serviceRegistry.register('task-estimation', () => new TaskEstimationService(), getServiceConfig('task-estimation'));
      serviceRegistry.register('task-dependency', () => new TaskDependencyService(), getServiceConfig('task-dependency'));
      serviceRegistry.register('recurrence', () => new RecurrenceService(), getServiceConfig('recurrence'));
      serviceRegistry.register('workload-tracking', () => new WorkloadTrackingService(), getServiceConfig('workload-tracking'));

      onProgress?.('Loading admin services...', 92);
      logger.debug('Registering audit and user services');
      serviceRegistry.register(
        'audit',
        () => new AuditService(),
        getServiceConfig('audit')
      );
      serviceRegistry.register(
        'user',
        () => new UserService(serviceRegistry),
        getServiceConfig('user')
      );

      onProgress?.('Finalizing workspace...', 95);
      logger.debug('Resolving final service dependencies');

      await Promise.all([
        serviceRegistry.get('notifications'),
        serviceRegistry.get('navigation'),
        serviceRegistry.get('workload-tracking'),
        serviceRegistry.get('time-block'),
        serviceRegistry.get('task-estimation'),
        serviceRegistry.get('task-dependency'),
        serviceRegistry.get('recurrence'),
        serviceRegistry.get('audit'),
        serviceRegistry.get('user'),
      ]);

      const duration = performance.now() - startTime;
      logger.info(`✅ Tier 2 services initialized in ${duration.toFixed(2)}ms`);

      onProgress?.('Ready!', 100);
      servicesFullyInitialized = true;
    } catch (error) {
      logger.error('❌ Failed to initialize authenticated services:', { error: error });
      // Clean up promise so we can retry if needed
      initializationPromise = null;
      throw error;
    }
  })();

  return initializationPromise;
};

/**
 * Authenticated Service Provider Component
 * Mounts only when user is authenticated
 */
export const AuthenticatedServiceProvider: FC<AuthenticatedServiceProviderProps> = ({ 
  children,
  onInitProgress
}) => {
  // Use global state initializer to avoid flickering if already ready
  const [ready, setReady] = useState(() => servicesFullyInitialized);
  
  // Ref to track if we've already kicked off initialization in this component lifecycle
  const initTriggered = useRef(false);

  useEffect(() => {
    // If we're already ready globally, just ensure local state matches and fire completion
    if (servicesFullyInitialized) {
      if (!ready) {setReady(true);}
      onInitProgress?.('Ready!', 100);
      return;
    }

    if (!initTriggered.current) {
        initTriggered.current = true;
        initializeAuthenticatedServices(onInitProgress)
            .then(() => {
                // Determine if we should set state
                // In StrictMode, this might run even if "unmounted", but since it's a global service
                // provider that *should* be available as long as user is logged in, setting state 
                // is generally safer than the black screen alternative.
                // React will warn if truly unmounted, but that's better than breakage.
                setReady(true);
            })
            .catch(() => {
                // Even on error, we might want to render content so the app isn't dead
                setReady(true);
            });
    }
    
    return () => {
        // We do NOT reset initializationPromise here.
        // We want the initialization to persist across re-mounts (Strict Mode).
        // Initialization reset happens only on explicit logout or page refresh.
    };
  }, [onInitProgress, ready]);

  if (!ready) {
    return null;
  }

  return <>{children}</>;
};
